import { defineStore } from 'pinia';
import ShopApi from '~/lib/shop-api';
import { useCartStore } from './cart';

export type ReductionMethod =
  | 'RS' // Discount amount, single unit (Voucher)
  | 'RT' // Discount amount, single unit (Coupon)
  | 'RP' // Percent
  | 'PS'; // New price, single unit

export type BaseCoupon = {
  description?: string;
  reductionMethodCode?: ReductionMethod;
  status: 'empty' | 'VALID' | 'PARTIALREDEEMED' | 'brandError' | 'INVALID' | 'EXPIRED';
  productNumbersWhitelist?: string[];
  productNumbersBlacklist?: string[];
  providersWhitelist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  providersBlacklist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  reductionValue?: number;
  type?: 'coupon' | 'voucher';
  typeCode?: '01' | '02'; // 01: apply-able on 1 product;
  initialValue?: number;
};

export interface Coupon extends BaseCoupon {
  couponCode: string;
  expiryDate?: string;
}

export interface RedeemedCouponResponse extends BaseCoupon {
  roundingMethodCode: string;
  multiple: string;
}

export interface CouponState {
  coupon: Coupon | undefined;
}

export const useCouponStore = defineStore('coupon', {
  state: (): CouponState => ({
    coupon: undefined,
  }),

  getters: {
    getCoupon(state) {
      return state.coupon;
    },
    couponInfo(state) {
      return state.coupon;
    },
    compatibleArticleNumbers(state) {
      return state.coupon?.productNumbersWhitelist || [];
    },
    incompatibleArticleNumbers(state) {
      return state.coupon?.productNumbersBlacklist || [];
    },
    compatibleProductProviders(state) {
      if (!state.coupon) return [];
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') &&
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom')
      ) {
        return [];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Brodos')
      ) {
        return ['TELEKOM'];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Telekom')
      ) {
        return ['BRODOS'];
      }
      return [];
    },
    isValidCoupon(state) {
      return ['valid', 'partialredeemed'].includes(state.coupon?.status?.toLowerCase() || '');
    },
    errorStatus(state) {
      return !this.isValidCoupon ? state.coupon?.status?.toLowerCase() : '';
    },
    reductionValue(state) {
      if (!state.coupon) return undefined;
      return state.coupon.reductionValue && state.coupon.reductionValue % 1 !== 0
        ? state.coupon.reductionValue.toFixed(2)
        : state.coupon.reductionValue;
    },
  },

  actions: {
    setCoupon(coupon: Coupon | undefined) {
      this.coupon = coupon;
    },
    eraseCoupon() {
      this.coupon = undefined;
    },
    setError(errorType: BaseCoupon['status']) {
      if (this.coupon) {
        this.coupon.status = errorType;
      }
    },

    async initializeCoupon(redeemedCoupon: string) {
      try {
        if (redeemedCoupon && redeemedCoupon !== this.coupon?.couponCode) {
          const data = await ShopApi.getCouponInfo(redeemedCoupon);
          this.setCoupon({
            ...data.couponVoucher,
            couponCode: redeemedCoupon,
          });
        }
      } catch (e) {
        console.error(e);
      }
    },

    async updateCoupon(couponCode: string) {
      try {
        const data = await ShopApi.getCouponInfo(couponCode);
        this.setCoupon({
          ...data.couponVoucher,
          couponCode: ['valid', 'partialredeemed'].includes(data.couponVoucher.status.toLowerCase())
            ? couponCode
            : '',
        });
        const cartStore = useCartStore();
        // Assuming 'cart/fetchCart' action exists in another store
        await cartStore.fetchCart();
      } catch (e) {
        console.error(e);
      }
    },

    async clearCoupon(isCart: boolean = false) {
      if (!this.coupon?.couponCode) return;
      this.eraseCoupon();
      // if the coupon is erased in the treueshop and not in the cart
      if (!isCart) {
        const cartStore = useCartStore();
        // Assuming 'cart/deleteCoupon' action exists in another store
        await cartStore.deleteCoupon();
      }
    },

    reportBrandError() {
      this.setError('brandError');
    },
  },
});
